import { GROCERY_API_PATHS, RESTAURANT_API_PATHS } from '../constants/apiPaths';
import axiosGroceryInstance from './AxiosGroceryInstance';
import axiosRestaurantInstance from './AxiosRestaurantInstance';
import { authHeaders } from './restaurantApi';
import { isSupermarketContext } from './entityContext';

function groceryAuthHeaders() {
  const token = localStorage.getItem('Token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function normalizePermission(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const permissionCode =
    raw.permissionCode ?? raw.Permission_Code ?? raw.code ?? raw.permission_code ?? '';
  if (!permissionCode) return null;
  return {
    permissionId: raw.permissionId ?? raw.Permission_Id ?? raw.id ?? null,
    permissionCode: String(permissionCode),
    name: raw.name ?? raw.Name ?? String(permissionCode),
    kind: String(raw.kind ?? raw.Kind ?? '').toUpperCase(),
    sortOrder: Number(raw.sortOrder ?? raw.Sort_Order ?? raw.sort_order ?? 0) || 0,
  };
}

function normalizeEntityRole(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const entityRoleId =
    raw.entityRoleId ?? raw.Entity_Role_Id ?? raw.entity_role_id ?? raw.id ?? null;
  if (entityRoleId == null || entityRoleId === '') return null;

  const permissionCodesRaw =
    raw.permissionCodes ??
    raw.permission_codes ??
    raw.Permission_Codes ??
    (Array.isArray(raw.permissions)
      ? raw.permissions.map((p) =>
          typeof p === 'string' ? p : p?.permissionCode ?? p?.Permission_Code ?? p?.code
        )
      : []);

  return {
    entityRoleId: Number(entityRoleId) || entityRoleId,
    roleName: String(raw.roleName ?? raw.Role_Name ?? raw.name ?? '').trim(),
    isSystem: Boolean(raw.isSystem ?? raw.Is_System ?? raw.is_system),
    status: String(raw.status ?? raw.Status ?? 'ACTIVE').toUpperCase(),
    permissionCodes: [
      ...new Set(
        (Array.isArray(permissionCodesRaw) ? permissionCodesRaw : [])
          .map((c) => String(c || '').trim())
          .filter(Boolean)
      ),
    ],
  };
}

export async function fetchPermissionCatalog(entityId, { isStore = isSupermarketContext() } = {}) {
  if (!entityId) return [];
  const path = isStore
    ? GROCERY_API_PATHS.storePermissions(entityId)
    : RESTAURANT_API_PATHS.restaurantPermissions(entityId);
  const client = isStore ? axiosGroceryInstance : axiosRestaurantInstance;
  const headers = isStore ? groceryAuthHeaders() : authHeaders();
  const response = await client.get(path, { headers });
  const payload = response.data;
  const list = Array.isArray(payload?.permissions)
    ? payload.permissions
    : Array.isArray(payload)
      ? payload
      : [];
  return list
    .map(normalizePermission)
    .filter(Boolean)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.permissionCode.localeCompare(b.permissionCode));
}

export async function fetchEntityRoles(entityId, { isStore = isSupermarketContext() } = {}) {
  if (!entityId) return [];
  const path = isStore
    ? GROCERY_API_PATHS.storeRoles(entityId)
    : RESTAURANT_API_PATHS.restaurantRoles(entityId);
  const client = isStore ? axiosGroceryInstance : axiosRestaurantInstance;
  const headers = isStore ? groceryAuthHeaders() : authHeaders();
  const response = await client.get(path, { headers });
  const payload = response.data;
  const list = Array.isArray(payload?.roles)
    ? payload.roles
    : Array.isArray(payload)
      ? payload
      : [];
  return list.map(normalizeEntityRole).filter(Boolean);
}

export async function createEntityRole(
  entityId,
  { roleName, permissionCodes },
  { isStore = isSupermarketContext() } = {}
) {
  const path = isStore
    ? GROCERY_API_PATHS.storeRoles(entityId)
    : RESTAURANT_API_PATHS.restaurantRoles(entityId);
  const client = isStore ? axiosGroceryInstance : axiosRestaurantInstance;
  const headers = {
    ...(isStore ? groceryAuthHeaders() : authHeaders()),
    'Content-Type': 'application/json',
  };
  return client.post(
    path,
    { roleName, permissionCodes: permissionCodes || [] },
    { headers }
  );
}

export async function updateEntityRole(
  entityId,
  entityRoleId,
  { roleName, permissionCodes },
  { isStore = isSupermarketContext() } = {}
) {
  const path = isStore
    ? GROCERY_API_PATHS.storeRoleById(entityId, entityRoleId)
    : RESTAURANT_API_PATHS.restaurantRoleById(entityId, entityRoleId);
  const client = isStore ? axiosGroceryInstance : axiosRestaurantInstance;
  const headers = {
    ...(isStore ? groceryAuthHeaders() : authHeaders()),
    'Content-Type': 'application/json',
  };
  return client.put(
    path,
    { roleName, permissionCodes: permissionCodes || [] },
    { headers }
  );
}

export async function deleteEntityRole(
  entityId,
  entityRoleId,
  { isStore = isSupermarketContext() } = {}
) {
  const path = isStore
    ? GROCERY_API_PATHS.storeRoleById(entityId, entityRoleId)
    : RESTAURANT_API_PATHS.restaurantRoleById(entityId, entityRoleId);
  const client = isStore ? axiosGroceryInstance : axiosRestaurantInstance;
  const headers = isStore ? groceryAuthHeaders() : authHeaders();
  return client.delete(path, { headers });
}
